/**
 * React Query hooks for managing report conversations.
 */
import { useQuery, useMutation, useQueryClient } from "react-query";
import { apiClient } from "@/lib/api";

// Types
export interface ConversationListItem {
    id: string;
    title: string;
    client_id: string | null;
    updated_at: string;
}

export interface ConversationMessage {
    id: number;
    role: "user" | "agent";
    content: string;
    sql_query: string | null;
    result_data: Array<Record<string, unknown>> | null;
    result_columns: string[] | null;
    created_at: string;
}

export interface ConversationDetail {
    id: string;
    title: string;
    client_id: string | null;
    created_at: string;
    updated_at: string;
    messages: ConversationMessage[];
}

// Query keys
const CONVERSATION_KEYS = {
    all: ["conversations"] as const,
    list: () => [...CONVERSATION_KEYS.all, "list"] as const,
    detail: (id: string) => [...CONVERSATION_KEYS.all, "detail", id] as const,
};

/**
 * Hook to fetch list of conversations for sidebar
 */
export const useConversationList = () => {
    return useQuery({
        queryKey: CONVERSATION_KEYS.list(),
        queryFn: async (): Promise<ConversationListItem[]> => {
            return apiClient.get<ConversationListItem[]>("/reports/conversations");
        },
        staleTime: 30 * 1000, // 30 seconds
        // Reports section is hidden for now — skip fetching to avoid extra requests.
        enabled: false,
    });
};

/**
 * Hook to fetch a single conversation with messages
 */
export const useConversation = (conversationId: string | null) => {
    return useQuery({
        queryKey: CONVERSATION_KEYS.detail(conversationId || ""),
        queryFn: async (): Promise<ConversationDetail> => {
            return apiClient.get<ConversationDetail>(`/reports/conversations/${conversationId}`);
        },
        enabled: !!conversationId,
    });
};

/**
 * Hook to rename a conversation
 */
export const useRenameConversation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, title }: { id: string; title: string }) => {
            return apiClient.patch<ConversationListItem>(`/reports/conversations/${id}`, { title });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: CONVERSATION_KEYS.list() });
        },
    });
};

/**
 * Hook to delete a conversation
 */
export const useDeleteConversation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            return apiClient.delete(`/reports/conversations/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: CONVERSATION_KEYS.list() });
        },
    });
};

/**
 * Hook to invalidate conversation list (e.g., after sending a message)
 */
export const useInvalidateConversations = () => {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: CONVERSATION_KEYS.list() });
};
